import { useEffect, useState } from 'react';
import { useAccountContext } from '@/app/accounts';
import { Icon } from '@/components/Icon';
import type { ComposeSeed } from '@/routes/mail/ComposeDialog';
import { ComposeDialog } from '@/app/lazyUi';
import { htmlToText } from '@/routes/mail/composeHtml';
import { formatBytes } from '@/utils/format';
import { useAccountOutbox } from './useOutbox';
import {
  EDIT_HOLD_MS,
  holdOutboxForEdit,
  releaseOutbox,
  removeOutbox,
  retryOutbox,
  touchOutboxHold,
} from './store';
import type { OutboxMessage } from './types';
import './outbox.css';

/**
 * The Outbox, at `/mail/outbox`.
 *
 * ## What it is for
 *
 * A queue the user cannot see is a queue they do not trust, and an app that
 * quietly holds someone's mail is worse than one that refuses to take it. So
 * every message here is openable, editable and removable, and every one of them
 * says what state it is in **in words** — "Waiting to send", not a spinner.
 *
 * ## What it is careful not to say
 *
 * Nothing here is ever described as sent. A message leaves this view at exactly
 * one moment: when the server has accepted a recipient for it. Until then the
 * strongest thing the view will say is that it is waiting.
 *
 * ## Editing
 *
 * "Edit" holds the record in the Outbox — it is not lifted out and put back,
 * because an offline composer cannot autosave a draft either, and for the
 * length of the edit the message would exist in nothing but React state. The
 * hold carries a deadline this view refreshes while the composer is open; a tab
 * that dies mid-edit lets it lapse and the message goes as it was, which is the
 * last thing the user actually instructed.
 */
export function OutboxView() {
  const { activeAccountId } = useAccountContext();
  const messages = useAccountOutbox(activeAccountId);
  const [editing, setEditing] = useState<{ id: string; seed: ComposeSeed } | undefined>();
  const [opened, setOpened] = useState<string | undefined>();

  // The edit hold's heartbeat. Cheap (one IndexedDB write a minute) and the
  // only thing standing between "this tab crashed mid-edit" and a message
  // stuck in the Outbox for ever.
  useEffect(() => {
    if (editing === undefined) return;
    const id = editing.id;
    const timer = setInterval(() => void touchOutboxHold(id), EDIT_HOLD_MS / 3);
    return () => clearInterval(timer);
  }, [editing]);

  const startEdit = async (message: OutboxMessage) => {
    const held = await holdOutboxForEdit(message.id);
    if (held === undefined) return; // Already sending: leave it alone.
    setEditing({ id: message.id, seed: seedFrom(message) });
  };

  const closeEdit = () => {
    const current = editing;
    setEditing(undefined);
    // Closed without sending: the message goes straight back on the queue,
    // unchanged. The user did not withdraw it, they just stopped looking.
    if (current !== undefined) void releaseOutbox(current.id);
  };

  return (
    <div className="outbox">
      <header className="outbox__header">
        <h1 className="outbox__title">Outbox</h1>
        <p className="outbox__lead">
          Messages stored on this device. Each one goes out on its own, in order, as soon as
          there is a connection — nothing here has been sent yet.
        </p>
      </header>

      {messages.length === 0 ? (
        <p className="muted outbox__empty">
          Nothing is waiting to send. Messages you send without a connection appear here until
          they go out.
        </p>
      ) : (
        <ul className="outbox__list">
          {messages.map((message) => (
            <li key={message.id} className="outbox__item" data-state={message.state}>
              <div className="outbox__row">
                <div className="outbox__summary">
                  <span className="outbox__to">{recipientSummary(message)}</span>
                  <span className="outbox__subject">{message.subject || '(no subject)'}</span>
                  <span className="outbox__state" data-state={message.state}>
                    <Icon name={message.state === 'failed' ? 'offline' : 'send'} size={14} />
                    {stateText(message)}
                  </span>
                </div>
                <div className="outbox__actions">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    aria-expanded={opened === message.id}
                    onClick={() => setOpened(opened === message.id ? undefined : message.id)}
                  >
                    {opened === message.id ? 'Close' : 'Open'}
                  </button>
                  {/*
                    Editing rebuilds the composer from the stored message, and
                    the composer's attachment strip can only be seeded with
                    server-staged files — so a message carrying local bytes has
                    no way back into it yet, and offering Edit would silently
                    send it without its attachments. Refused with a reason
                    instead; Remove and re-compose is the path until the
                    composer can be seeded with local files.
                  */}
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={message.state === 'sending' || message.attachments.length > 0}
                    title={
                      message.attachments.length > 0
                        ? 'A message with attachments has to be removed and written again.'
                        : undefined
                    }
                    onClick={() => void startEdit(message)}
                  >
                    Edit
                  </button>
                  {message.state === 'failed' && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void retryOutbox(message.id)}
                    >
                      Try again
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--ghost btn--danger"
                    disabled={message.state === 'sending'}
                    onClick={() => void removeOutbox(message.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>

              {message.failure !== undefined && (
                <p className="outbox__failure" role={message.state === 'failed' ? 'alert' : undefined}>
                  {message.failure.message}
                  {message.failure.rejected !== undefined &&
                    message.failure.rejected.length > 0 && (
                      <>
                        {' '}
                        {message.failure.rejected
                          .map((one) => `${one.address}: ${one.message}`)
                          .join('; ')}
                      </>
                    )}
                </p>
              )}

              {opened === message.id && (
                <div className="outbox__preview">
                  <dl className="outbox__fields">
                    <dt>From</dt>
                    <dd>{message.fromAddress}</dd>
                    <dt>To</dt>
                    <dd>{addressText(message.to) || '—'}</dd>
                    {message.cc.length > 0 && (
                      <>
                        <dt>Cc</dt>
                        <dd>{addressText(message.cc)}</dd>
                      </>
                    )}
                    {message.bcc.length > 0 && (
                      <>
                        <dt>Bcc</dt>
                        <dd>{addressText(message.bcc)}</dd>
                      </>
                    )}
                    {message.attachments.length > 0 && (
                      <>
                        <dt>Attachments</dt>
                        <dd>
                          {message.attachments
                            .map((one) => `${one.filename} (${formatBytes(one.sizeBytes)})`)
                            .join(', ')}
                        </dd>
                      </>
                    )}
                  </dl>
                  {/*
                    Plain text, not the stored HTML. The body was composed in
                    this app and is about to be sanitized server-side anyway,
                    but rendering untrusted-shaped markup into the app's own
                    document is a habit worth not having (ADR 0049).
                  */}
                  <pre className="outbox__body">{bodyPreview(message)}</pre>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing !== undefined && (
        <ComposeDialog
          open
          onClose={closeEdit}
          accountId={activeAccountId}
          fromAddress={messages.find((one) => one.id === editing.id)?.fromAddress}
          initial={editing.seed}
          continued
          outboxId={editing.id}
        />
      )}
    </div>
  );
}

/** What the row says the message is doing, in words a person would use. */
export function stateText(message: OutboxMessage): string {
  switch (message.state) {
    case 'held':
      return 'Waiting to send';
    case 'queued':
      return message.attempts > 0 ? 'Waiting to try again' : 'Waiting to send';
    case 'sending':
      return 'Sending…';
    case 'failed':
      return "Couldn't be sent";
  }
}

function addressText(addresses: OutboxMessage['to']): string {
  return addresses.map((one) => one.name || one.email).join(', ');
}

function recipientSummary(message: OutboxMessage): string {
  const all = [...message.to, ...message.cc, ...message.bcc];
  if (all.length === 0) return '(no recipients)';
  const first = all[0]!;
  const rest = all.length - 1;
  const name = first.name || first.email;
  return rest > 0 ? `${name} +${rest}` : name;
}

function bodyPreview(message: OutboxMessage): string {
  if (message.bodyText.trim() !== '') return message.bodyText;
  return message.bodyHtml ? htmlToText(message.bodyHtml) : '';
}

/** Rebuilds a composer seed from a stored message. */
export function seedFrom(message: OutboxMessage): ComposeSeed {
  return {
    to: addressList(message.to),
    cc: addressList(message.cc),
    bcc: addressList(message.bcc),
    subject: message.subject,
    body: message.bodyText,
    ...(message.bodyHtml ? { bodyHtml: message.bodyHtml } : {}),
    ...(message.inReplyToMessageId
      ? { inReplyToMessageId: message.inReplyToMessageId }
      : {}),
    ...(message.forwardedMessageId
      ? { forwardedMessageId: message.forwardedMessageId }
      : {}),
    mode: message.mode,
    ...(message.draftId ? { draftId: message.draftId } : {}),
    // The Nextcloud links in its body come back as rows (ADR 0084 D13).
    ...(message.nextcloudUploadIds?.length ? { nextcloudUploadIds: message.nextcloudUploadIds } : {}),
  };
}

function addressList(addresses: OutboxMessage['to']): string {
  return addresses.map((one) => (one.name ? `${one.name} <${one.email}>` : one.email)).join(', ');
}
